import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Keeps one broken page from taking down the whole portal.
 *
 * React unmounts the entire tree when a render throws and nothing catches it.
 * In the browser that shows as a blank tab the user reloads without thinking;
 * inside the Institute EXE it is a blank WINDOW with no address bar, no reload
 * button and no way back, and the institute reports that "the EXE stopped
 * working". It was reproduced exactly that way: the instructor dashboard read
 * `summary.subject_count` from a local answer that had no `summary`, threw, and
 * every one of the fifty pages visited afterwards was empty — not because they
 * were broken, but because there was no longer an app to render them.
 *
 * So a page that throws is contained to its own panel: the sidebar, the session
 * selector and every other page keep working, and the operator is told what
 * happened rather than being left looking at nothing.
 *
 * Keyed on the route by its caller, so simply navigating away clears it.
 */
type Props = { children: ReactNode; routeKey?: string };
type State = { error: Error | null };

/**
 * A chunk that no longer exists is a deployment, not a defect.
 *
 * Every build gives the lazily-loaded pages new content-hashed filenames, so a
 * deploy strands the names any already-open tab is still holding. Navigating to
 * such a page then fails with "Failed to fetch dynamically imported module",
 * which reaches the operator as a technical sentence about a URL and leaves
 * them with "Try this page again" — the one button that cannot help, because
 * the file really is gone.
 */
const STALE_CHUNK = /failed to (fetch|load) dynamically imported module|importing a module script failed|error loading dynamically imported module/i;
const isStaleChunkError = (error: Error): boolean =>
    STALE_CHUNK.test(String(error?.message || ''));

/**
 * Reload at most once per window, so a chunk that is genuinely broken — rather
 * than merely renamed — shows its error instead of refreshing for ever.
 */
const RELOAD_KEY = 'portal:staleChunkReloadAt';
const RELOAD_WINDOW_MS = 30_000;

function reloadOnceForStaleChunk(): boolean {
    let last = 0;
    // Private windows and blocked site data make sessionStorage throw on
    // access, which must not turn a recoverable error into a second one.
    try { last = Number(window.sessionStorage.getItem(RELOAD_KEY) || 0); } catch { /* unavailable */ }
    if (Date.now() - last < RELOAD_WINDOW_MS) return false;
    try { window.sessionStorage.setItem(RELOAD_KEY, String(Date.now())); } catch { /* unavailable */ }
    window.location.reload();
    return true;
}

export default class PageErrorBoundary extends Component<Props, State> {
    state: State = { error: null };

    static getDerivedStateFromError(error: Error): State {
        return { error };
    }

    componentDidUpdate(prev: Props) {
        // A different page is a fresh start; without this the boundary would
        // keep showing the failure after the operator had navigated away.
        if (prev.routeKey !== this.props.routeKey && this.state.error) {
            this.setState({ error: null });
        }
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        // The app was updated while this tab was open. Fetching index.html again
        // is the whole fix, so do it rather than asking the operator to work
        // that out from a URL they have no way to interpret.
        if (isStaleChunkError(error) && reloadOnceForStaleChunk()) return;

        // Kept to the console on purpose: this is what the EXE's own log and a
        // support screenshot are read from, and it is the only record of the
        // component that failed.
        console.error('[page] render failed:', error?.message, info?.componentStack);
    }

    render() {
        const { error } = this.state;
        if (!error) return this.props.children;

        // Only reached for a stale chunk when the automatic reload has already
        // been tried and the page still failed, so say what that means instead
        // of repeating a sentence about a URL.
        const stale = isStaleChunkError(error);

        return (
            <div className="portal-page animate-in">
                <div className="card max-w-2xl">
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                        {stale ? 'The app was updated — this page needs reloading' : 'This page could not be displayed'}
                    </h2>
                    <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                        {stale
                            ? 'A new version was released while this page was open, so part of it is no longer available. Reloading loads the new version. Nothing you have saved is affected.'
                            : 'Everything else is still working — use the menu to carry on, and tell support what you were doing when this appeared.'}
                    </p>
                    <p className="mt-3 rounded bg-slate-100 p-3 font-mono text-xs break-words text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                        {error?.message || String(error)}
                    </p>
                    {/* For a stale chunk, re-rendering cannot help: the file is
                        genuinely gone. Offering it first would be offering the
                        one button that does nothing, so Reload leads instead. */}
                    <div className="mt-4 flex flex-wrap gap-2">
                        {stale ? (
                            <button
                                type="button"
                                className="btn-primary !w-auto"
                                onClick={() => window.location.reload()}
                            >
                                Reload the app
                            </button>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    className="btn-primary !w-auto"
                                    onClick={() => this.setState({ error: null })}
                                >
                                    Try this page again
                                </button>
                                <button
                                    type="button"
                                    className="btn-secondary !w-auto"
                                    onClick={() => window.location.reload()}
                                >
                                    Reload
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}
